import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiService from '../services/apiService'
import logService from '../services/logService'
import { useUIStore } from './ui'

export const useWhyChooseUsStore = defineStore('whyChooseUs', () => {
  const uiStore = useUIStore()

  // State
  const features = ref([])
  const loading = ref(false)
  const error = ref(null)

  // Getters
  const activeFeatures = computed(() => features.value.filter(f => f.is_active))
  const sortedFeatures = computed(() => [...features.value].sort((a, b) => a.order - b.order))

  // Actions
  /**
   * Fetch all active features (public endpoint)
   */
  const fetchFeatures = async () => {
    loading.value = true
    error.value = null
    try {
      logService.info('Fetching why choose us features...')
      const response = await apiService.get('/why-choose-us-features')
      if (response.success) {
        features.value = response.data
      }
    } catch (err) {
      error.value = err.message
      logService.error('Error fetching why choose us features:', err)
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch all features including inactive (admin endpoint)
   */
  const fetchAllFeatures = async () => {
    loading.value = true
    error.value = null
    try {
      logService.info('Fetching all why choose us features...')
      const response = await apiService.get('/why-choose-us-features/all')
      if (response.success) {
        features.value = response.data
      }
    } catch (err) {
      error.value = err.message
      logService.error('Error fetching all why choose us features:', err)
    } finally {
      loading.value = false
    }
  }

  /**
   * Create a new feature
   */
  const createFeature = async (featureData) => {
    try {
      logService.info('Creating feature...', featureData)
      const response = await apiService.post('/why-choose-us-features', featureData)
      if (response.success) {
        await fetchAllFeatures()

        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Feature created successfully'
        })

        return response.data
      }
    } catch (err) {
      error.value = err.message
      logService.error('Error creating feature:', err)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: err.message
      })
      throw err
    }
  }

  /**
   * Update an existing feature
   */
  const updateFeature = async (id, featureData) => {
    try {
      logService.info(`Updating feature: ${id}`, featureData)
      const response = await apiService.put(`/why-choose-us-features/${id}`, featureData)
      if (response.success) {
        await fetchAllFeatures()

        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Feature updated successfully'
        })

        return response.data
      }
    } catch (err) {
      error.value = err.message
      logService.error('Error updating feature:', err)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: err.message
      })
      throw err
    }
  }

  /**
   * Delete a feature
   */
  const deleteFeature = async (id) => {
    try {
      logService.info(`Deleting feature: ${id}`)
      const response = await apiService.delete(`/why-choose-us-features/${id}`)
      if (response.success) {
        await fetchAllFeatures()

        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Feature deleted successfully'
        })

        return true
      }
    } catch (err) {
      error.value = err.message
      logService.error('Error deleting feature:', err)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: err.message
      })
      throw err
    }
  }

  /**
   * Update the order of features
   */
  const updateFeaturesOrder = async (featuresList) => {
    try {
      logService.info('Updating feature order...')
      const response = await apiService.post('/why-choose-us-features/update-order', { features: featuresList })
      if (response.success) {
        await fetchAllFeatures()

        uiStore.showNotification({
          type: 'success',
          title: 'Success',
          message: 'Feature order updated successfully'
        })

        return true
      }
    } catch (err) {
      error.value = err.message
      logService.error('Error updating feature order:', err)
      uiStore.showNotification({
        type: 'error',
        title: 'Error',
        message: err.message
      })
      throw err
    }
  }

  return {
    // State
    features,
    loading,
    error,

    // Getters
    activeFeatures,
    sortedFeatures,

    // Actions
    fetchFeatures,
    fetchAllFeatures,
    createFeature,
    updateFeature,
    deleteFeature,
    updateFeaturesOrder
  }
})
